import type { PaymentMethod, StoredMethodType } from '@neon-one/auctions-types';
import {
  OPAQUE_STORED_METHOD_TYPES,
  storedMethodTypeLabel,
} from '@neon-one/auctions-types';

/** Rails that carry no brand/last4 at all — the wallet name IS the label. */
const OPAQUE_RAILS: ReadonlySet<StoredMethodType> = new Set<StoredMethodType>(
  OPAQUE_STORED_METHOD_TYPES,
);

/**
 * The rail's user-facing name.
 *
 * Delegates to `storedMethodTypeLabel` so bidder-web, settlement and admin
 * winners cannot drift on the noun. This module keeps the
 * bidder-facing PHRASING ("Visa ending 4242") and the icon set; only the
 * vocabulary is shared.
 */
export function methodTypeLabel(methodType: StoredMethodType | null | undefined): string {
  return storedMethodTypeLabel(methodType);
}

/** PrimeIcons class for a rail. Used wherever `pi pi-credit-card` was fixed. */
export function methodTypeIcon(methodType: PaymentMethod | null | undefined): string {
  switch (methodType) {
    case 'ach':
      return 'pi pi-building-columns';
    case 'apple_pay':
      return 'pi pi-apple';
    case 'google_pay':
      return 'pi pi-google';
    case 'paypal':
      return 'pi pi-paypal';
    case 'venmo':
      return 'pi pi-wallet';
    case 'cash':
      return 'pi pi-money-bill';
    case 'check':
      return 'pi pi-file';
    case 'wire':
      return 'pi pi-building-columns';
    case 'terminal':
    case 'card':
    default:
      return 'pi pi-credit-card';
  }
}

export interface PaymentMethodLike {
  methodType?: StoredMethodType | null;
  /** Card brand, or the bank name for ACH (see RegistrationInput docs). */
  brand?: string | null;
  /** Card or bank-account tail. */
  last4?: string | null;
}

/**
 * Human label for a stored payment method.
 *
 *   card  + Visa + 4242 → "Visa ending 4242"
 *   ach   + Chase + 6789 → "Chase ending 6789"
 *   ach   + null  + 6789 → "Bank account ending 6789"
 *   apple_pay            → "Apple Pay"   (never "… ending ••••")
 */
export function paymentMethodLabel(
  pm: PaymentMethodLike,
  opts: { expired?: boolean } = {},
): string {
  const suffix = opts.expired ? ' (expired)' : '';
  const methodType = pm.methodType ?? 'card';
  if (OPAQUE_RAILS.has(methodType)) return `${methodTypeLabel(methodType)}${suffix}`;
  const name = pm.brand?.trim() || methodTypeLabel(methodType);
  const last4 = pm.last4?.trim();
  return last4 ? `${name} ending ${last4}${suffix}` : `${name}${suffix}`;
}
